import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import logo from '../../logo/lo.png';
import { crm } from './crmApi';
import { homePage, loadCrmSession, saveCrmSession } from './session';
import type { CrmSession } from './session';
import { forgetCrmMe } from './ui';

// /crm/login — one password box for everyone in sales: the admin password,
// or a Salesperson / Lead Manager / Sales Admin passcode
const CrmLogin: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const existing = loadCrmSession();
  if (existing) return <Navigate to={`/crm/${homePage(existing)}`} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) { toast.error('Enter your password or passcode'); return; }
    setBusy(true);
    try {
      const r = await crm.login(password.trim());
      const session: CrmSession = r.kind === 'admin'
        ? { kind: 'admin', name: 'Admin', role: 'Admin', isManager: true, takesLeads: false, adminPassword: password.trim() }
        : { kind: 'sales', id: r.salesPersonId || '', name: r.name, role: r.role as Exclude<CrmSession['role'], 'Admin'>, isManager: r.isManager, takesLeads: r.takesLeads, crmToken: r.crmToken || '' };
      forgetCrmMe();
      saveCrmSession(session);
      toast.success(`Welcome, ${session.name}`);
      navigate(`/crm/${homePage(session)}`, { replace: true });
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: 'linear-gradient(160deg,#1a1428 0%,#2b2240 55%,#3d2f5f 100%)' }}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <span className="w-12 h-12 rounded-xl bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
            <img src={logo} alt="" className="w-[170%] max-w-none h-auto" />
          </span>
          <div>
            <p className="text-white font-bold text-lg leading-tight">Sales CRM</p>
            <p className="text-white/50 text-xs">VistaTech Solutions</p>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-6 space-y-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Sign in</h1>
            <p className="text-xs text-gray-500 mt-1">Admin: the admin password. Salespeople, Lead Managers and Sales Admins: your passcode.</p>
          </div>
          <div>
            <label htmlFor="crm-pass" className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Password or passcode</label>
            <div className="relative">
              <input id="crm-pass" type={show ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} autoFocus autoComplete="current-password"
                className="w-full pl-4 pr-16 py-3 border-2 border-gray-200 rounded-xl text-base outline-none focus:border-purple-400" />
              <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 hover:text-gray-600">{show ? 'Hide' : 'Show'}</button>
            </div>
          </div>
          <button type="submit" disabled={busy} className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="text-[11px] text-gray-400 text-center">No passcode yet? The admin gives one on the Salespeople page.</p>
        </form>
      </div>
    </div>
  );
};

export default CrmLogin;
